// src/Admin/AddProduct.jsx
import { useState } from "react";

const COLORS = ["White", "Black", "Blue", "Red", "Pink", "Green", "Yellow", "Gray", "Navy"];
const SIZES = ["XS", "S", "M", "L", "XL", "XXL", "XXXL", "One Size"];

export default function AddProduct() {
  const [name, setName] = useState("");
  const [price, setPrice] = useState("");
  const [offerPrice, setOfferPrice] = useState("");
  const [description, setDescription] = useState("");
  const [isBestSeller, setIsBestSeller] = useState(false);

  // Dynamic variants (one per color-size combo)
  const [variants, setVariants] = useState([
    { color: "White", size: "M", stock: 50, image: null, imagePreview: null }
  ]);

  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Add new variant row
  const addVariant = () => {
    setVariants([...variants, { color: "Black", size: "M", stock: 0, image: null, imagePreview: null }]);
  };

  // Remove variant
  const removeVariant = (index) => {
    setVariants(variants.filter((_, i) => i !== index));
  };

  // Update variant field
  const updateVariant = (index, field, value) => {
    const updated = [...variants];
    updated[index][field] = value;
    setVariants(updated);
  };

  // Handle image change with preview
  const handleImageChange = (index, file) => {
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const updated = [...variants];
        updated[index].image = file;
        updated[index].imagePreview = reader.result;
        setVariants(updated);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setMessage("");

    // Validation
    if (!name || !price) {
      setMessage("Product name and price are required.");
      setIsSubmitting(false);
      return;
    }

    if (variants.some(v => !v.image)) {
      setMessage("Please upload an image for every variant.");
      setIsSubmitting(false);
      return;
    }

    const formData = new FormData();
    formData.append("name", name);
    formData.append("price", price);
    if (offerPrice) formData.append("offer_price", offerPrice);
    if (description) formData.append("description", description);
    if (isBestSeller) formData.append("is_best_seller", "true");

    // Append variants as JSON
    const variantsJSON = variants.map(v => ({
      color: v.color,
      size: v.size || null,
      stock: parseInt(v.stock) || 0,
    }));
    formData.append("variants", JSON.stringify(variantsJSON));

    // Append images with index keys
    variants.forEach((variant, index) => {
      if (variant.image) {
        formData.append(`image_${index}`, variant.image);
      }
    });

    try {
      const response = await fetch("/api/create-product/", {
        method: "POST",
        headers: {
          // Let browser set Content-Type for multipart
        },
        body: formData,
      });

      const result = await response.json();

      if (response.ok) {
        setMessage("Product added successfully!");
        // Reset form
        setName(""); setPrice(""); setOfferPrice(""); setDescription(""); setIsBestSeller(false);
        setVariants([{ color: "White", size: "M", stock: 50, image: null, imagePreview: null }]);
        setTimeout(() => setMessage(""), 5000);
      } else {
        setMessage(result.detail || "Failed to add product.");
      }
    } catch (err) {
      console.error(err);
      setMessage("Network error. Is backend running?");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-pink-50 py-12 px-4">
      <div className="max-w-5xl mx-auto">
        <h1 className="text-4xl font-bold text-center text-gray-800 mb-10">Add New Product</h1>

        <form onSubmit={handleSubmit} className="bg-white rounded-3xl shadow-2xl p-8 border border-gray-100">
          {/* Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Product Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-5 py-3 border-2 border-gray-200 rounded-xl focus:border-black focus:outline-none transition"
                placeholder="Premium Cotton T-Shirt"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Regular Price (₹)</label>
              <input
                type="number"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                className="w-full px-5 py-3 border-2 border-gray-200 rounded-xl focus:border-black"
                placeholder="1499"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Offer Price (₹) <span className="text-gray-500 font-normal">(optional)</span></label>
              <input
                type="number"
                value={offerPrice}
                onChange={(e) => setOfferPrice(e.target.value)}
                className="w-full px-5 py-3 border-2 border-gray-200 rounded-xl focus:border-black"
                placeholder="999"
              />
            </div>
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={isBestSeller}
                onChange={(e) => setIsBestSeller(e.target.checked)}
                className="w-6 h-6 text-black rounded focus:ring-black"
              />
              <label className="text-lg font-semibold text-gray-700">Mark as Best Seller</label>
            </div>
          </div>

          <div className="mb-8">
            <label className="block text-sm font-bold text-gray-700 mb-2">Description (optional)</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full px-5 py-3 border-2 border-gray-200 rounded-xl focus:border-black"
              placeholder="Soft, breathable cotton..."
            />
          </div>

          {/* Variants */}
          <div className="mb-8">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-2xl font-bold text-gray-800">Variants (Color + Size + Stock)</h3>
              <button
                type="button"
                onClick={addVariant}
                className="px-5 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition"
              >
                + Add Variant
              </button>
            </div>

            <div className="space-y-6">
              {variants.map((variant, index) => (
                <div key={index} className="bg-gradient-to-r from-gray-50 to-gray-100 p-6 rounded-2xl border-2 border-gray-200">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                    {/* Color */}
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-1">Color</label>
                      <select
                        value={variant.color}
                        onChange={(e) => updateVariant(index, "color", e.target.value)}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-black"
                      >
                        {COLORS.map(c => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </select>
                    </div>

                    {/* Size */}
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-1">Size</label>
                      <select
                        value={variant.size || ""}
                        onChange={(e) => updateVariant(index, "size", e.target.value || null)}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-black"
                      >
                        <option value="">Select Size</option>
                        {SIZES.map(s => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </div>

                    {/* Stock */}
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-1">Stock</label>
                      <input
                        type="number"
                        value={variant.stock}
                        onChange={(e) => updateVariant(index, "stock", e.target.value)}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-black"
                        min="0"
                        placeholder="50"
                      />
                    </div>

                    {/* Remove */}
                    {variants.length > 1 && (
                      <div className="flex items-end">
                        <button
                          type="button"
                          onClick={() => removeVariant(index)}
                          className="px-4 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 transition"
                        >
                          Remove
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Image Upload */}
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">Image for {variant.color} {variant.size && `(${variant.size})`}</label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageChange(index, e.target.files[0])}
                      className="block w-full text-sm text-gray-600 file:mr-4 file:py-3 file:px-6 file:rounded-lg file:border-0 file:bg-black file:text-white hover:file:bg-gray-800"
                      required
                    />
                    {variant.imagePreview && (
                      <div className="mt-3">
                        <img src={variant.imagePreview} alt="Preview" className="h-32 rounded-lg shadow-md object-cover" />
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={isSubmitting}
            className={`w-full py-5 text-xl font-bold text-white rounded-2xl transition-all transform hover:scale-105 ${
              isSubmitting
                ? "bg-gray-500 cursor-not-allowed"
                : "bg-gradient-to-r from-black to-gray-800 shadow-2xl"
            }`}
          >
            {isSubmitting ? "Saving Product..." : "Create Product"}
          </button>
        </form>

        {/* Message */}
        {message && (
          <div className={`mt-8 p-6 rounded-2xl text-center text-lg font-bold ${
            message.includes("success")
              ? "bg-green-100 text-green-800 border-2 border-green-300"
              : "bg-red-100 text-red-800 border-2 border-red-300"
          }`}>
            {message}
          </div>
        )}

        <div className="text-center mt-8">
          <a href="/collections" className="text-lg text-gray-600 hover:text-black underline font-medium">
            Back to Collections
          </a>
        </div>
      </div>
    </div>
  );
}